import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { foodPreferencesService } from '../../../../services/foodPreferencesInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('FoodPreferencesInput');

type FoodPreferencesInputScreenRouteProp = RouteProp<RootStackParamList, 'FoodPreferencesInput'>;

export const FoodPreferencesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<FoodPreferencesInputScreenRouteProp>();

  const { foodPreferencesData, isEditing } = route.params || {};

  const [foodPreferences, setFoodPreferences] = useState(
    foodPreferencesData?.foodPreferences || ''
  );
  const [dietaryRestrictions, setDietaryRestrictions] = useState(
    foodPreferencesData?.dietaryRestrictions || ''
  );
  const [favoriteDishes, setFavoriteDishes] = useState(
    foodPreferencesData?.favoriteDishes || ''
  );
  const [beveragePreferences, setBeveragePreferences] = useState(
    foodPreferencesData?.beveragePreferences || ''
  );
  const [specialRequests, setSpecialRequests] = useState(
    foodPreferencesData?.specialRequests || ''
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!foodPreferences.trim()) {
      Alert.alert('Error', 'Please enter at least your food preferences');
      return;
    }

    try {
      setSaving(true);

      const foodPreferencesUpdateData = {
        foodPreferences: foodPreferences.trim(),
        dietaryRestrictions: dietaryRestrictions.trim(),
        favoriteDishes: favoriteDishes.trim(),
        beveragePreferences: beveragePreferences.trim(),
        specialRequests: specialRequests.trim(),
      };

      let savedFoodPreferences;

      if (isEditing && foodPreferencesData?.id) {
        // Update existing record
        savedFoodPreferences = await foodPreferencesService.updateFoodPreferences(
          foodPreferencesData.id,
          foodPreferencesUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedFoodPreferences?.data || savedFoodPreferences;
        logger.debug('Food preferences updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        navigation.navigate('FoodPreferencesLockUnlock', { foodPreferencesId: updatedData.id });
      } else {
        // Create new record
        savedFoodPreferences = await foodPreferencesService.createFoodPreferences(foodPreferencesUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedFoodPreferences?.data || savedFoodPreferences;
        logger.debug('Food preferences saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('FoodPreferencesLockUnlock', { foodPreferencesId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving food preferences:', error);
      Alert.alert('Error', 'Failed to save food preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Food & Drink Preferences
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Food Preferences:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={foodPreferences}
              onChangeText={setFoodPreferences}
              placeholder="What types of food would you like served? (e.g., favorite cuisine, specific dishes, catering style)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Dietary Restrictions:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={dietaryRestrictions}
              onChangeText={setDietaryRestrictions}
              placeholder="Any dietary restrictions to consider? (e.g., vegetarian, vegan, gluten-free, allergies)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Favorite Dishes:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={favoriteDishes}
              onChangeText={setFavoriteDishes}
              placeholder="Any specific dishes you'd like included? (e.g., grandma's recipe, favorite restaurant dish)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Beverage Preferences:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={beveragePreferences}
              onChangeText={setBeveragePreferences}
              placeholder="What beverages would you like served? (e.g., coffee, tea, wine, cocktails, non-alcoholic options)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Requests:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={specialRequests}
              onChangeText={setSpecialRequests}
              placeholder="Any other special requests? (e.g., favorite caterer, restaurant, presentation style)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});

export default FoodPreferencesInputScreen;